import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Text, Stack, HStack, Badge, Icon, Spinner, Button } from "@chakra-ui/react";
import { ClipboardList, MapPin, CalendarClock, AlertTriangle, FileText, CheckCircle2, PenLine } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { AcknowledgeModal } from "@/components/AcknowledgeModal";
import { colors } from "@/theme/colors";

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");
const today = () => new Date().toISOString().slice(0, 10);
const isOverdue = (a) => a.status !== "completed" && a.due_date < today();

function TaskCard({ a, mySignoff, onStart, onSign }) {
  const over = isOverdue(a);
  return (
    <Box bg={colors.white} border="1px solid" borderColor={over ? colors.poppy : colors.border} borderRadius="12px" p="4" data-testid={`task-${a.id}`}>
      <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
        <HStack gap="3" minW="0" align="flex-start">
          <Flex align="center" justify="center" w="38px" h="38px" borderRadius="10px" bg={over ? colors.poppyTint : colors.tealTint} flexShrink={0}>
            <Icon as={a.kind === "resource" ? FileText : ClipboardList} boxSize="4.5" color={over ? colors.poppy : colors.teal} />
          </Flex>
          <Box minW="0">
            <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>{a.title}</Text>
            <HStack gap="3" flexWrap="wrap" mt="0.5">
              {a.location_name && <HStack gap="1"><Icon as={MapPin} boxSize="3" color={colors.textSecondary} /><Text fontSize="xs" color={colors.textSecondary}>{a.location_name}</Text></HStack>}
              <HStack gap="1">
                <Icon as={over ? AlertTriangle : CalendarClock} boxSize="3" color={over ? colors.poppy : colors.textSecondary} />
                <Text fontSize="xs" color={over ? colors.poppy : colors.textSecondary} fontWeight={over ? "700" : "400"}>Due {fmtDate(a.due_date)}</Text>
              </HStack>
              {a.recurrence !== "once" && <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" textTransform="capitalize">{a.recurrence}</Badge>}
            </HStack>
            {a.instructions && <Text fontSize="xs" color={colors.textSecondary} mt="1.5" fontStyle="italic">"{a.instructions}"</Text>}
            {Array.isArray(a.additional_signers) && a.additional_signers.length > 0 && (
              <Text fontSize="xs" color={colors.textSecondary} mt="1">Additional signatures required: {a.additional_signers.map((s) => s.name).join(", ")}</Text>
            )}
          </Box>
        </HStack>
        <Flex gap="2" align="center">
          {over && <Badge bg={colors.criticalBg} color={colors.criticalText} borderRadius="full" px="2.5">Overdue</Badge>}
          {a.kind === "resource" ? (
            <Button size="sm" onClick={() => onSign(a, mySignoff)} disabled={!mySignoff} data-testid={`task-ack-${a.id}`}>View & sign</Button>
          ) : (
            <Button size="sm" onClick={() => onStart(a)} data-testid={`task-start-${a.id}`}>Start</Button>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}

function SignRequestCard({ so, onSign }) {
  const a = so.form_assignments;
  if (!a) return null;
  const ready = a.status !== "assigned";
  const over = isOverdue(a);
  return (
    <Box bg={colors.white} border="1px solid" borderColor={over ? colors.poppy : colors.border} borderRadius="12px" p="4" data-testid={`signoff-task-${so.id}`}>
      <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
        <HStack gap="3" minW="0" align="flex-start">
          <Flex align="center" justify="center" w="38px" h="38px" borderRadius="10px" bg="#FBEEDA" flexShrink={0}>
            <Icon as={PenLine} boxSize="4.5" color="#8A5106" />
          </Flex>
          <Box minW="0">
            <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>{a.title}</Text>
            <HStack gap="3" flexWrap="wrap" mt="0.5">
              <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px">{a.kind === "resource" ? "Document" : "Form"}</Badge>
              <Text fontSize="xs" color={colors.textSecondary}>Assigned to {a.worker_name}</Text>
              <HStack gap="1">
                <Icon as={over ? AlertTriangle : CalendarClock} boxSize="3" color={over ? colors.poppy : colors.textSecondary} />
                <Text fontSize="xs" color={over ? colors.poppy : colors.textSecondary} fontWeight={over ? "700" : "400"}>Due {fmtDate(a.due_date)}</Text>
              </HStack>
            </HStack>
            <Text fontSize="xs" color={ready ? "#0F6E56" : colors.textSecondary} mt="1" data-testid={`signoff-ready-${so.id}`}>
              {ready ? "Ready for your signature" : `Waiting for ${a.worker_name} to complete this first`}
            </Text>
          </Box>
        </HStack>
        <Flex gap="2" align="center">
          {over && <Badge bg={colors.criticalBg} color={colors.criticalText} borderRadius="full" px="2.5">Overdue</Badge>}
          <Button size="sm" onClick={() => onSign(a, so)} disabled={!ready} data-testid={`signoff-sign-${so.id}`}>Review & sign</Button>
        </Flex>
      </Flex>
    </Box>
  );
}

// Worker's "Assigned to you" tasks + "Needs your sign-off" requests.
// `compact` renders a short combined list for Home dashboards.
export function AssignedTasks({ compact = false }) {
  const navigate = useNavigate();
  const { user, activeCompanyId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [signRequests, setSignRequests] = useState([]);
  const [mySignoffs, setMySignoffs] = useState({});
  const [loadFailed, setLoadFailed] = useState(false);
  const [signing, setSigning] = useState(null); // { assignment, signoff }

  const load = useCallback(async () => {
    const applySnap = () => {
      // Offline: serve the last-known-good tasks instead of an empty state.
      const s = cacheGet(user.id, "tasks") || {};
      setTasks(s.tasks || []);
      setSignRequests(s.signRequests || []);
      setMySignoffs(s.mySignoffs || {});
      setLoadFailed(true);
    };
    // Tenant isolation: resolve MY worker record within the ACTING company only —
    // a multi-membership user has worker rows in several companies.
    const { data: wk, error: wErr } = await safeQuery(supabase.from("workers").select("id")
      .eq("profile_id", user.id).eq("company_id", activeCompanyId).limit(1));
    if (wErr) { applySnap(); setLoading(false); return; }
    const wid = wk?.[0]?.id || null;
    if (!wid) { setTasks([]); setSignRequests([]); setLoadFailed(false); setLoading(false); return; }
    const [faRes, soRes] = await safeAll([
      supabase.from("form_assignments").select("*").eq("worker_id", wid).eq("status", "assigned").order("due_date"),
      supabase.from("assignment_signoffs").select("*, form_assignments(*)")
        .eq("worker_id", wid).eq("status", "pending").order("created_at"),
    ]);
    if (faRes.error || soRes.error) { applySnap(); setLoading(false); return; }
    const all = soRes.data || [];
    const tasksArr = faRes.data || [];
    const sr = all.filter((s) => s.signer_role === "additional" && s.form_assignments && !["completed", "superseded"].includes(s.form_assignments.status));
    const ms = Object.fromEntries(all.filter((s) => s.signer_role === "assignee").map((s) => [s.assignment_id, s]));
    setTasks(tasksArr);
    setSignRequests(sr);
    setMySignoffs(ms);
    setLoadFailed(false);
    cachePut(user.id, "tasks", { tasks: tasksArr, signRequests: sr, mySignoffs: ms });
    setLoading(false);
  }, [user.id, activeCompanyId]);
  useEffect(() => { load(); }, [load]);

  const start = (a) => {
    const p = new URLSearchParams({ assignment: a.id });
    if (a.location_id) p.set("loc", a.location_id);
    navigate(`/forms/fill/${a.template_id}?${p.toString()}`);
  };
  const openSign = (assignment, signoff) => setSigning({ assignment, signoff });

  if (loading) return compact ? null : <Flex minH="30vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;

  const total = tasks.length + signRequests.length;
  if (compact && total === 0) return null;
  if (!compact && total === 0) {
    return (
      <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="6" textAlign="center" data-testid="tasks-empty">
        <Icon as={CheckCircle2} boxSize="6" color={colors.sageDeep} mb="2" />
        <Text fontWeight="600" color={colors.textPrimary}>{loadFailed ? "You're offline" : "You're all caught up"}</Text>
        <Text fontSize="sm" color={colors.textSecondary}>{loadFailed ? "Your tasks and sign-offs will appear once this device has loaded them." : "No tasks or sign-offs waiting for you."}</Text>
      </Box>
    );
  }

  const shownTasks = compact ? tasks.slice(0, 3) : tasks;
  const shownSigns = compact ? signRequests.slice(0, Math.max(0, 4 - shownTasks.length)) : signRequests;

  return (
    <Stack gap="4" data-testid="assigned-tasks">
      {shownTasks.length > 0 && (
        <Stack gap="2.5">
          {shownTasks.map((a) => (
            <TaskCard key={a.id} a={a} mySignoff={mySignoffs[a.id]} onStart={start} onSign={openSign} />
          ))}
        </Stack>
      )}
      {shownSigns.length > 0 && (
        <Box data-testid="signoff-requests">
          <Text fontSize="sm" fontWeight="700" color={colors.textPrimary} mb="2">Needs your sign-off</Text>
          <Stack gap="2.5">
            {shownSigns.map((so) => <SignRequestCard key={so.id} so={so} onSign={openSign} />)}
          </Stack>
        </Box>
      )}
      {signing && (
        <AcknowledgeModal signoff={signing.signoff} assignment={signing.assignment}
          onClose={() => setSigning(null)} onDone={() => { setSigning(null); load(); }} />
      )}
    </Stack>
  );
}

export default function Tasks() {
  return (
    <Box data-testid="tasks-page" maxW="720px">
      <Text fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>Assigned to you</Text>
      <Text color={colors.textSecondary} mb="5" fontSize={{ base: "sm", md: "md" }}>Forms, documents to acknowledge and sign-offs waiting for you.</Text>
      <AssignedTasks />
    </Box>
  );
}
